import { useEffect, useState } from 'react';
import { Link, Outlet, useSearch } from '@tanstack/react-router';
import { subscribeToVisibleJobs, portalShows, type Job } from '@buildmate/core';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';

/**
 * The frame the homeowner's pages draw inside.
 *
 * Extracted from `ClientPortal` when the schedule became a page of its own
 * (P4-21). Until then the portal was one component and the header lived in
 * it, which is fine for one page and wrong for two — a second page would
 * either lose the header or own a copy of it.
 *
 * The navigation will not grow much: a homeowner is not administering
 * anything, and `docs/features/10-client-portal.md` §3 lists the module
 * pages that may eventually join them. Each is a link rather than a tab
 * control because each is a real address — a schedule somebody can send to
 * their spouse is worth more than one that only exists as component state.
 *
 * **Budget appears only when a job shares it** (P5-08). It is off by
 * default — `DEFAULT_PORTAL_MODULES.budget` is `false`, because most
 * fixed-price jobs should not expose a cost-code breakdown — so a
 * permanent link would send most owners to a page explaining that they
 * cannot see it. Schedule is different and stays unconditional: it is on
 * by default, and a job whose schedule has not been published yet is a
 * normal, temporary state the page can explain. "Your builder has not
 * shared this" reads as a promise when it is temporary and as a dead end
 * when it is not.
 */
export function PortalLayout() {
  const ports = usePorts();
  const { company, user, membership } = useSession();
  /**
   * The project being looked at, carried from page to page (SCRUM-297).
   *
   * Read off the URL rather than held here, because the frame is not
   * the thing that knows: every portal page already takes `job` as a
   * search parameter so a link can be sent. What was missing was the
   * navigation passing it on — so a client picked a project, clicked a
   * tab, and arrived back at the first one.
   */
  const { job } = useSearch({ strict: false }) as { job?: string };
  const [jobs, setJobs] = useState<readonly Job[]>([]);

  useEffect(
    () =>
      subscribeToVisibleJobs(
        { data: ports.data, companyId: membership.companyId },
        membership,
        setJobs,
        // A failed read costs a navigation link, not the portal. The page
        // behind it says the same thing.
        () => {},
      ),
    [ports, membership],
  );

  const sharesBudget = jobs.some((job) => portalShows(job, 'budget'));
  /**
   * Invoices are **on** by default, unlike the budget.
   *
   * So the link is unconditional in the same way the schedule's is: a job
   * with no invoices yet is a normal, temporary state the page can explain,
   * and "nothing to pay yet" reads as a relief rather than a dead end.
   */
  const sharesInvoices = jobs.some((job) => portalShows(job, 'invoices'));

  return (
    <div className="min-h-full bg-slate-50">
      <header className="bg-slate-900 px-4 py-3 text-white">
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-semibold tracking-tight">{company?.name ?? 'Your builder'}</span>
          {/* `flex-wrap`, and it is load-bearing. Adding the fifth link
              (Warranty, P8-03) pushed this nav past 400px: the page
              scrolled sideways on a phone and the link that stuck out past
              the painted header failed axe's contrast rule, because what
              is behind it there is the body's near-white rather than
              slate-900. CI found it before I did — Linux renders this font
              a little wider — which is the same way the main navigation's
              overflow was found. `header-fits.e2e.ts` now measures this
              header too. */}
          <nav aria-label="Your project" className="flex flex-wrap gap-1 text-sm">
            <PortalLink job={job} to="/portal" label="Project" />
            <PortalLink job={job} to="/portal/schedule" label="Schedule" />
            {sharesBudget && <PortalLink job={job} to="/portal/budget" label="Budget" />}
            {sharesInvoices && <PortalLink job={job} to="/portal/invoices" label="Invoices" />}
            {/* Unconditional, and there is no `portalModules.warranties` to
                make it otherwise. A builder who could switch off the place
                a homeowner reports a defect has not stopped the defect
                being reported, only moved it to a voicemail. */}
            <PortalLink job={job} to="/portal/warranties" label="Warranty" />
            {/* Unconditional like the others. A sheet with nothing shared
                yet is a normal, temporary state the page explains, and
                "your builder has not shared this yet" reads as a promise
                when it is temporary. */}
            <PortalLink job={job} to="/portal/specifications" label="Specifications" />
          </nav>
          <span className="ml-auto text-sm text-slate-300">{user.displayName ?? user.email}</span>
        </div>
      </header>

      <Outlet />
    </div>
  );
}

/**
 * One navigation link, active only on an exact match.
 *
 * Both halves of `activeOptions` are load-bearing, and the e2e found each
 * of them in turn:
 *
 * - `exact: true`, because `Link` sets `aria-current="page"` itself when
 *   its route is active and a **parent** route counts as active for its
 *   children. Without it, `/portal/schedule` had *both* links claiming to
 *   be the current page, and a screen reader announcing two current pages
 *   is worse than announcing none.
 * - `includeSearch: false`, because exact matching compares the search
 *   parameters too. The schedule link carries none and the page's URL
 *   carries `?view=…&job=…`, so with search included *neither* link was
 *   ever active — the opposite failure, from the fix for the first one.
 *
 * The highlight comes from `activeProps` rather than a pathname comparison
 * so there is one source of truth for "is this the page I am on". Two
 * answers is how the attribute and the colour came to disagree.
 */
function PortalLink({
  to,
  label,
  job,
}: {
  /** The project being looked at, so a tab does not change it. */
  readonly job?: string;
  readonly to:
    | '/portal'
    | '/portal/schedule'
    | '/portal/budget'
    | '/portal/invoices'
    | '/portal/warranties'
    | '/portal/specifications';
  readonly label: string;
}) {
  return (
    <Link
      to={to}
      search={job ? { job } : {}}
      activeOptions={{ exact: true, includeSearch: false }}
      className="rounded-md px-3 py-1 text-slate-300 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      activeProps={{ className: 'rounded-md px-3 py-1 bg-slate-700 font-medium text-white' }}
    >
      {label}
    </Link>
  );
}
